import { useRef } from "react";
import styled from "styled-components";
import { BASIC_GRAY_BACKGROUND, BASIC_LINE } from "../../../constants/colors";
import { Category } from "../../../ducks/menu";
import { useCart } from "../../../hooks/useCart";
import { useCoupon } from "../../../hooks/useCoupon";
import { CartIndicator } from "../../molecules/menu/CartIndicator";
import { CouponInput } from "../../molecules/menu/CouponInput";
import { PickUpDetail } from "../../molecules/store/PickUpDetail";
import { StoreDetail } from "../../molecules/store/StoreDetail";
import { CartDetailPopup } from "../../organisms/menu/CartDetailPopup";
import { MenuCategoriesList } from "../../organisms/menu/MenuCategoriesList";
import { MenuListByCategory } from "../../organisms/menu/MenuListByCategory";
import { RecommendedMenuPopup } from "../../organisms/menu/RecommendedMenuPopup";
import { useMenuList } from "./hook";

type Props = {
  showAppCouponHeader: boolean;
};

export const MenuList: React.FC<Props> = ({ showAppCouponHeader }) => {
  const useCouponHook = useCoupon();
  const {
    appliedCoupons,
    applyCoupon,
    getCouponName,
    handleRemoveCoupon,
    handleUseAppCoupon,
  } = useCouponHook;

  const {
    categoriesToDisplay,
    store,
    date,
    pickupDateText,
    recommendedMenus,
    limitedMenus,
    handleSetFavoriteStore,
    navigateToTopPage,
    handleOnClickChangePickupDate,
    handleSetFavoriteMenu,
    menuMapById,
  } = useMenuList(useCouponHook);

  const {
    menusInCart,
    orderedMenus,
    totalItemsInCart,
    totalPriceInCart,
    totalTaxInCart,
    orderCoupon,
    showCart,
    shouldAnimate,
    showCartPopup,
    setShowCartPopup,
    selectedLimitedMenus,
    recommendedMenusToShow,
    showRecommendedMenuPopup,
    setShowRecommendedMenuPopup,
    handleClickCartIndicator,
    showCartOrderContent,
    setShowCartOrderContent,
    handleAddItemToCart,
    handleUpdateItem,
    handleRemoveItem,
  } = useCart(
    store,
    useCouponHook,
    menuMapById,
    recommendedMenus,
    limitedMenus,
  );

  const categoryRefs = useRef<{
    [key: string]: { el: HTMLDivElement | null; data: Category };
  }>({});

  return (
    <MenuListContainer $hasAppCoupon={showAppCouponHeader}>
      <StoreDetailSectionWrapper>
        <StoreDetailSection>
          {store && (
            <>
              <StoreDetail
                id={store.id}
                name={store.name}
                address={store.prefecture + store.city + store.address}
                phoneNumber={store.phoneNumber}
                service={store.service}
                location={{
                  lat: store.location.latitude,
                  lng: store.location.longitude,
                }}
                isAdvancedPayment={store.isPaymentAvailable}
                isFavorite={store.isFavorite}
                onFavoriteClick={handleSetFavoriteStore}
                showChangeButton
                onClickChangeButton={navigateToTopPage}
              />
              <StoreSeparator />
            </>
          )}

          {store && date && (
            <PickUpDetail
              pickupDateText={pickupDateText}
              onClickChangeButton={handleOnClickChangePickupDate}
            />
          )}

          {store && date && (
            <CouponInput
              onUseAppCoupon={() =>
                handleUseAppCoupon({
                  storeId: store.id,
                  pickupDate: date,
                  storeShortestTimeUntilPickup: store.shortestTimeUntilPickup,
                  redirectUrl: window.location.href,
                })
              }
              onApplyCoupon={(couponCode) =>
                applyCoupon(store.id, date, store.shortestTimeUntilPickup, [
                  couponCode,
                ])
              }
              onRemoveCoupon={handleRemoveCoupon}
              getCouponName={getCouponName}
              appliedCoupons={appliedCoupons}
            />
          )}
        </StoreDetailSection>
      </StoreDetailSectionWrapper>

      {categoriesToDisplay.length > 0 && (
        <>
          <MenuCategoriesList
            categories={categoriesToDisplay}
            categoryRefs={categoryRefs}
            showAppCouponHeader={showAppCouponHeader}
          />
          {categoriesToDisplay.map((category) => (
            <PaddedContainer key={category.id}>
              <MenuListByCategory
                categoryRef={(el) =>
                  (categoryRefs.current[category.id] = { el, data: category })
                }
                category={category}
                menus={category.menus}
                selectedLimitedMenus={selectedLimitedMenus}
                onFavorite={handleSetFavoriteMenu}
                onSelectMenu={handleAddItemToCart}
              />
            </PaddedContainer>
          ))}
        </>
      )}

      {store && date && menuMapById && menusInCart && (
        <CartDetailPopup
          isShow={showCartPopup}
          menusInCart={menusInCart}
          orderedMenus={orderedMenus}
          selectedLimitedMenus={selectedLimitedMenus}
          menuMapById={menuMapById}
          shouldAnimate={shouldAnimate}
          showCartOrderContent={showCartOrderContent}
          totalPriceInCart={totalPriceInCart}
          totalItemsInCart={totalItemsInCart}
          totalTaxInCart={totalTaxInCart}
          orderCoupon={orderCoupon}
          handleClickCartOrderContent={() =>
            setShowCartOrderContent((prev) => !prev)
          }
          handleCloseCartPopup={() => setShowCartPopup(false)}
          handleFavorite={handleSetFavoriteMenu}
          handleUpdateItem={handleUpdateItem}
          handleRemoveItem={handleRemoveItem}
          onRemoveCoupon={handleRemoveCoupon}
          getCouponName={getCouponName}
          appliedCoupons={appliedCoupons}
        />
      )}

      {recommendedMenus.length > 0 && (
        <RecommendedMenuPopup
          isShow={showRecommendedMenuPopup}
          showAppCouponHeader={showAppCouponHeader}
          menus={recommendedMenusToShow}
          selectedLimitedMenus={selectedLimitedMenus}
          onFavorite={handleSetFavoriteMenu}
          onSelectMenu={handleAddItemToCart}
          onClose={() => setShowRecommendedMenuPopup(false)}
        />
      )}

      <PaddedContainer>
        <CartIndicator
          isShow={showCart}
          showCartPopup={showCartPopup}
          showRecommendedMenuPopup={showRecommendedMenuPopup}
          onClick={handleClickCartIndicator}
          totalQuantity={totalItemsInCart}
          totalPrice={totalPriceInCart}
          couponPrice={orderCoupon?.price}
          animate={shouldAnimate}
        />
      </PaddedContainer>
    </MenuListContainer>
  );
};

const MenuListContainer = styled.div<{ $hasAppCoupon: boolean }>`
  display: flex;
  flex-direction: column;
  margin: ${({ $hasAppCoupon }) => ($hasAppCoupon ? "100px auto 0px" : "0")};
  padding: ${({ $hasAppCoupon }) => ($hasAppCoupon ? "0" : "0 0 32px 0")};
  background-color: #f4f4f4;
`;

const PaddedContainer = styled.div`
  padding: 0 10px;
`;

const StoreDetailSectionWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-self: center;
  background-color: ${BASIC_GRAY_BACKGROUND};
  padding-top: 16px;
  top: 52px;
  width: 100vw;
  min-width: 375px;
`;

const StoreDetailSection = styled.div`
  width: 100%;
  max-width: 980px;
  margin: 0 16px;
`;

const StoreSeparator = styled.div`
  border-bottom: 1px solid ${BASIC_LINE};
  margin-bottom: 16px;
`;
